<template>
  <div class="main">
    <!-- Top bar -->
    <vue-file-toolbar-menu :content="menu"
                           class="bar"/>

    <!-- Document editor -->
    <!-- eslint-disable vue/no-v-model-argument -->
    <vue-document-editor class="editor"
                         ref="editor"
                         :content.sync="content"
                         :zoom="zoom"
                         :editable="false"
                         :page_format_mm="page_format_mm"
                         :page_margins="page_margins"
                         :display="display"/>

  </div>
</template>

<script>
/* eslint-disable camelcase,eqeqeq */

import VueDocumentEditor from 'vue-document-editor'
import VueFileToolbarMenu from 'vue-file-toolbar-menu'

export default {
  name: 'Test1',
  components: {
    VueDocumentEditor,
    VueFileToolbarMenu,
  },
  data () {
    return {
      // This is where the pages content is stored and synced
      content: [
        // Every item below produce a page break
        `<div>
<h1 contenteditable="true">Hello world!</h1>
<p>This is a rich-text editor built on top of <p/>
<p>This is a rich-text editor built on top of <p/>
<p>This is a rich-text editor built on top of <p/>
<p>This is a rich-text editor built on top of <p/>
<p>This is a rich-text editor built on top of <p/>
<p>This is a rich-text editor built on top of <p/>

</div>`,
      ],
      zoom: 1,
      zoom_min: 0.10,
      zoom_max: 5.0,
      page_format_mm: [210, 297],
      page_margins: '10mm 15mm',
      display: 'grid', // ["grid", "vertical", "horizontal"]
      mounted: false, // will be true after this component is mounted
      undo_count: -1, // contains the number of times user can undo (= current position in content_history)
      content_history: [], // contains the content states for undo/redo operations
    }
  },
  created () {
    // Initialize gesture flags
    let start_zoom_gesture = false
    let start_dist_touch = false
    let start_zoom_touch = false
    // Manage ctrl+scroll zoom (or trackpad pinch)
    window.addEventListener('wheel', (e) => {
      if (e.ctrlKey) {
        e.preventDefault()
        this.zoom = Math.min(Math.max(this.zoom - e.deltaY * 0.01, this.zoom_min), this.zoom_max)
      }
    }, { passive: false })
    // Manage trackpad pinch on Safari
    window.addEventListener('gesturestart', (e) => {
      e.preventDefault()
      start_zoom_gesture = this.zoom
    })
    window.addEventListener('gesturechange', (e) => {
      e.preventDefault()
      if (!start_zoom_touch) {
        this.zoom = Math.min(Math.max(start_zoom_gesture * e.scale, this.zoom_min), this.zoom_max)
      }
    })
    window.addEventListener('gestureend', () => {
      start_zoom_gesture = false
    })
    // Manage pinch to zoom for touch devices
    window.addEventListener('touchstart', (e) => {
      if (e.touches.length == 2) {
        e.preventDefault()
        start_dist_touch = Math.hypot(
          e.touches[0].pageX - e.touches[1].pageX,
          e.touches[0].pageY - e.touches[1].pageY,
        )
        start_zoom_touch = this.zoom
      }
    }, { passive: false })
    window.addEventListener('touchmove', (e) => {
      if (start_dist_touch && start_zoom_touch) {
        e.preventDefault()
        const zoom = start_zoom_touch * Math.hypot(
          e.touches[0].pageX - e.touches[1].pageX,
          e.touches[0].pageY - e.touches[1].pageY,
        ) / start_dist_touch
        this.zoom = Math.min(Math.max(zoom, this.zoom_min), this.zoom_max)
      }
    }, { passive: false })
    window.addEventListener('touchend', () => {
      start_dist_touch = false
      start_zoom_touch = false
    }, { passive: false })
    // Manage history undo/redo events
    const manage_undo_redo = (e) => {
      switch (e && e.inputType) {
        case 'historyUndo':
          e.preventDefault()
          e.stopPropagation()
          this.undo()
          break
        case 'historyRedo':
          e.preventDefault()
          e.stopPropagation()
          this.redo()
          break
      }
    }
    window.addEventListener('beforeinput', manage_undo_redo)
    window.addEventListener('input', manage_undo_redo) // in case of beforeinput event is not implemented (Firefox)
    // If your component is susceptible to be destroyed, don't forget to
    // use window.removeEventListener in the Vue.js beforeUnmount handler
  },
  mounted () {
    this.mounted = true
  },
  computed: {
    // This is the menu content
    menu () {
      return [
        {
          title: 'Undo',
          icon: 'undo',
          disabled: !this.can_undo,
          hotkey: this.isMacLike ? 'command+z' : 'ctrl+z',
          click: () => this.undo(),
        },
        {
          title: 'Redo',
          icon: 'redo',
          disabled: !this.can_redo,
          hotkey: this.isMacLike ? 'shift+command+z' : 'ctrl+y',
          click: () => this.redo(),
        },
        {
          icon: 'format_align_left',
          title: 'Align left',
          active: this.isLeftAligned,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? 'shift+command+l' : 'ctrl+shift+l',
          click: () => document.execCommand('justifyLeft'),
        },
        {
          icon: 'format_align_center',
          title: 'Align center',
          active: this.isCentered,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? 'shift+command+e' : 'ctrl+shift+e',
          click: () => document.execCommand('justifyCenter'),
        },
        {
          icon: 'format_align_right',
          title: 'Align right',
          active: this.isRightAligned,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? 'shift+command+r' : 'ctrl+shift+r',
          click: () => document.execCommand('justifyRight'),
        },
        {
          icon: 'format_align_justify',
          title: 'Justify content',
          active: this.isJustified,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? 'shift+command+j' : 'ctrl+shift+j',
          click: () => document.execCommand('justifyFull'),
        },
        { is: 'separator' },
        {
          icon: 'format_bold',
          title: 'Bold',
          active: this.isBold,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? 'command+b' : 'ctrl+b',
          click: () => document.execCommand('bold'),
        },
        {
          icon: 'format_italic',
          title: 'Italic',
          active: this.isItalic,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? 'command+i' : 'ctrl+i',
          click: () => document.execCommand('italic'),
        },
        {
          icon: 'format_underline',
          title: 'Underline',
          active: this.isUnderline,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? 'command+u' : 'ctrl+u',
          click: () => document.execCommand('underline'),
        },
        {
          icon: 'format_strikethrough',
          title: 'Strike through',
          active: this.isStrikeThrough,
          disabled: !this.current_text_style,
          click: () => document.execCommand('strikethrough'),
        },
        {
          is: 'button-color',
          type: 'compact',
          menu_class: 'align-center',
          disabled: !this.current_text_style,
          color: this.curColor,
          update_color: (new_color) => document.execCommand('foreColor', false, new_color.hex8),
        },
        { is: 'separator' },
        {
          icon: 'format_list_numbered',
          title: 'Numbered list',
          active: this.isNumberedList,
          disabled: !this.current_text_style,
          click: () => document.execCommand('insertOrderedList'),
        },
        {
          icon: 'format_list_bulleted',
          title: 'Bulleted list',
          active: this.isBulletedList,
          disabled: !this.current_text_style,
          click: () => document.execCommand('insertUnorderedList'),
        },
        {
          icon: 'format_clear',
          title: 'Clear format',
          disabled: !this.current_text_style,
          click () {
            document.execCommand('removeFormat')
            document.execCommand('formatBlock', false, '<div>')
          },
        },
      ]
    },
    // Current text style management
    current_text_style () {
      return this.mounted ? this.$refs.editor.current_text_style : false
    },
    isLeftAligned () {
      return ['start', 'left', '-moz-left'].includes(this.current_text_style.textAlign)
    },
    isRightAligned () {
      return ['end', 'right', '-moz-right'].includes(this.current_text_style.textAlign)
    },
    isCentered () {
      return ['center', '-moz-center'].includes(this.current_text_style.textAlign)
    },
    isJustified () {
      return ['justify', 'justify-all'].includes(this.current_text_style.textAlign)
    },
    isBold () {
      const fontWeight = this.current_text_style.fontWeight
      return fontWeight && (parseInt(fontWeight) > 400 || fontWeight.indexOf('bold') == 0)
    },
    isItalic () {
      return this.current_text_style.fontStyle == 'italic'
    },
    isUnderline () { // text-decoration is not overridden by children, so we query the parent stack
      const stack = this.current_text_style.textDecorationStack
      return stack && stack.some(d => (d.indexOf('underline') == 0))
    },
    isStrikeThrough () { // text-decoration is not overridden by children, so we query the parent stack
      const stack = this.current_text_style.textDecorationStack
      return stack && stack.some(d => (d.indexOf('line-through') == 0))
    },
    isNumberedList () {
      return this.current_text_style.isList && this.current_text_style.listStyleType == 'decimal'
    },
    isBulletedList () {
      return this.current_text_style.isList && ['disc', 'circle'].includes(this.current_text_style.listStyleType)
    },
    isH1 () {
      return this.current_text_style.headerLevel == 1
    },
    isH2 () {
      return this.current_text_style.headerLevel == 2
    },
    isH3 () {
      return this.current_text_style.headerLevel == 3
    },
    curColor () {
      return this.current_text_style.color || 'transparent'
    },
    // Platform management
    isMacLike: () => /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform),
    // Undo / redo flags
    can_undo () {
      return this.undo_count > 0
    },
    can_redo () {
      return this.content_history.length - this.undo_count - 1 > 0
    },
  },
  methods: {
    // Undo / redo functions examples
    undo () {
      if (this.can_undo) {
        this._mute_next_content_watcher = true
        this.content = this.content_history[--this.undo_count]
      }
    },
    redo () {
      if (this.can_redo) {
        this._mute_next_content_watcher = true
        this.content = this.content_history[++this.undo_count]
      }
    },
    resetContentHistory () {
      this.content_history = []
      this.undo_count = -1
    },
  },
  watch: {
    content: {
      immediate: true,
      // Fill undo / redo history stack on user input
      handler (new_content) {
        if (!this._mute_next_content_watcher) { // only update the stack when content is changed by user input, not undo/redo commands
          this.content_history[++this.undo_count] = new_content
          this.content_history.length = this.undo_count + 1 // remove all redo items
        }
        this._mute_next_content_watcher = false
      },
    },
  },
}
</script>

<style lang="scss">

body {
  //margin: 0;
  ////font-family: Avenir, Helvetica, Arial, sans-serif;
  //-webkit-font-smoothing: antialiased;
  //-moz-osx-font-smoothing: grayscale;
  //color: black;
  //background: rgb(248, 249, 250);
}

</style>

<style lang="scss"
       scoped>
@import "../../assets/css/bulma-theme";

.editor {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  background: $white-ter;
  height: 100%;

  ::v-deep .page {
    background: $white;
    box-shadow: var(--page-box-shadow, 0 1px 3px 1px rgba(60, 64, 67, .15));
    //-webkit-box-shadow: 0px 0px 10px 1px rgba(0,0,0,0.8);
    //box-shadow: 0px 0px 10px 1px rgba(0,0,0,0.8);
  }

  ::v-deep *[contenteditable='true'] {
    background: $warning-light;
    transition: padding 0.05s linear;
  }
}

span[contenteditable]:focus {
  padding: 0 4px;
}

.main {
  width: fit-content;
  min-width: 100%;
}

.bar {
  position: sticky;
  justify-content: center;
  left: 0;
  top: 0;
  width: calc(100vw - 16px);
  z-index: 1000;
  background: rgba(248, 249, 250, 0.8);
  border-bottom: solid 1px rgb(248, 249, 250);
  backdrop-filter: blur(10px);
  --bar-button-active-color: #188038;
  --bar-button-open-color: #188038;
  --bar-button-active-bkg: #e6f4ea;
  --bar-button-open-bkg: #e6f4ea;
}
</style>
